import { default as React } from 'react';
import { useContext, useEffect, useState } from 'react';
import {
  DevHubFormControl,
  DevHubAutocomplete,
  DevHubTextField,
  DevHubCheckbox,
  DevHubFormControlLabel,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';
import { CustomFieldExtensionSchema } from '@backstage/plugin-scaffolder-react';
import { slugify } from '../../util/stringFormat';
import { gql, useLazyQuery } from '@apollo/client';
import { UserContext } from '../../contexts/UserContext';

type App = {
  applicationId: string;
  name: string;
  appslug?: string;
  status: string;
};

type Tenancy = {
  id: string;
  name: string;
};

/**
 * Schema definition for the OCP Config File Picker custom field extension.
 * This schema defines the structure of the return value for the field extension.
 */
export const OCPConfigFilePickerSchema: CustomFieldExtensionSchema = {
  returnValue: {
    type: 'object',
    properties: {
      applicationId: { type: 'number', title: 'Comcast Application ID' },
      applicationName: { type: 'string' },
      appslug: { type: 'string' },
      metrics: {
        type: 'object',
        properties: {
          enablemetric: { type: 'boolean' },
          tenancyName: { type: 'string' },
          metricrawurl: { type: 'string' },
          metricendpointurl: { type: 'string' },
          ingestenabled: { type: 'boolean' },
          ingestUrl: { type: 'string' },
          vmagenturl: { type: 'string' },
          metricinstanceid: { type: 'string' },
          remoteWriteUsername: { type: 'string' },
        },
      },
    },
  },
};

/**
 * A custom field extension component for selecting and configuring OCP metrics.
 *
 * @param props - The props for the field extension component.
 */
export const OCPConfigFilePicker = (
  props: FieldExtensionComponentProps<
    any,
    {
      appApi?: string;
      configFileApi?: string;
    }
  >,
) => {
  const { onChange, formData, idSchema, required } = props;
  const { login } = useContext(UserContext);

  const [apps, setApps] = useState<App[]>([]);
  const [selectedApp, setSelectedApp] = useState<App | null>(
    formData?.applicationId ? formData : null,
  );

  const [enableMetrics, setEnableMetrics] = useState(false);
  const [tenancies, setTenancies] = useState<Tenancy[]>([]);
  const [tenancyFileMap, setTenancyFileMap] = useState<any[]>([]);
  const [metricsTenancy, setMetricsTenancy] = useState<Tenancy | null>(null);
  const [ocpInstanceOptions, setOcpInstanceOptions] = useState<any[]>([]);
  const [selectedInstance, setSelectedInstance] = useState<any | null>(null);
  const [metricsFile, setMetricsFile] = useState<string | null>(null);
  const [loadingTenancies, setLoadingTenancies] = useState(false);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [remoteWriteUrl, setRemoteWriteUrl] = useState('');
  const [OCPInstanceId, setOCPInstanceId] = useState('');
  const [remoteWriteUsername, setRemoteWriteUsername] = useState('');
  const [VMAgentUrl, setVMAgentUrl] = useState('');
  const [IngestfieldEnabled, setIngestfieldEnabled] = useState(false);
  const [isIngestUrlChecked, setIsIngestUrlChecked] = useState(false);
  const [ingestUrl, setIngestUrl] = useState('');
  const [remoteWriteUrlLoader, setRemoteWriteUrlLoader] = useState(false);

  const GetUserRoleBasedApplicationsQuery = gql`
    query GetUserRoleBasedApplications(
      $login: String!
      $filter: ResourceCatalogUserApplicationFilter
    ) {
      identity {
        user(login: $login) {
          resources {
            applications(filter: $filter) {
              name
              applicationId
              status
            }
          }
        }
      }
    }
  `;

  const filterRetired = (app: App): boolean => app.status !== 'Retired';

  const [fetchApplications, { data, loading }] = useLazyQuery(
    GetUserRoleBasedApplicationsQuery,
    {
      errorPolicy: 'all',
      variables: {
        login,
        filter: {
          havingRoles: [
            'owner',
            'assistant_owner',
            'approver',
            'devops_remediation',
            'developer',
          ],
        },
      },
    },
  );

  const [errors, setErrors] = React.useState({
    metricsFile: '',
    ingestUrl: '',
  });

  const validateMetricsFile = (value: string) => {
    let metricsFileError = '';
    if (!value) {
      metricsFileError = 'Metrics Config File URL is required';
    } else if (
      !/^https:\/\/raw\.githubusercontent\.com\/.*\.yaml$/.test(value)
    ) {
      metricsFileError =
        "URL must start with 'https://raw.githubusercontent.com/' and end with '.yaml'";
    }
    setErrors(prev => ({ ...prev, metricsFile: metricsFileError }));

    return metricsFileError === '';
  };

  const validateIngestUrl = (value: string) => {
    let ingestUrlError = '';
    if (!value) {
      ingestUrlError = 'Ingest URL is required';
    } else if (!value.startsWith('https://ingest')) {
      ingestUrlError = "Ingest URL must start with 'https://ingest'";
    }
    setErrors(prev => ({ ...prev, ingestUrl: ingestUrlError }));
    return ingestUrlError === '';
  };

  useEffect(() => {
    if (login) fetchApplications();
    if (data) {
      const appsCopy: App[] =
        data?.identity?.user?.resources?.applications?.map((app: App) =>
          Object.assign({}, app, { appslug: slugify(app.name) }),
        ) ?? [];
      appsCopy.sort((a, b) => a.name.localeCompare(b.name));
      setApps(appsCopy.filter(filterRetired));
    }
  }, [login, data, fetchApplications]);

  useEffect(() => {
    if (!selectedApp?.applicationId) return;

    const fetchTenancyAndFiles = async () => {
      try {
        setLoadingTenancies(true);
        const response = await fetch(
          `/api/proxy/cnap/tenancy/getOCPTenantInformation?devhubid=${selectedApp.applicationId}`,
        );
        const jsonResponse = await response.json();
        const transformed = (jsonResponse || []).map((item: any) => ({
          tenancyname: item.OCPTenancyName,
          tenancyId: item.OCPTenancyId,
          gitRepo: item.GITREPO,
          tenancySlug: item.OCPTenancySlug,
        }));

        setTenancyFileMap(transformed);
        setTenancies(
          transformed.map((item: any) => ({
            id: item.tenancyname,
            name: item.tenancyname,
          })),
        );
        setLoadingTenancies(false);
      } catch (err) {
        setLoadingTenancies(false);
        setTenancyFileMap([]);
        setTenancies([]);
      }
    };

    fetchTenancyAndFiles();
  }, [selectedApp]);

  useEffect(() => {
    const metrics: any = enableMetrics
      ? {
          enablemetric: true,
          tenancyName: metricsTenancy?.name,
          metricrawurl: metricsFile,
          metricendpointurl: remoteWriteUrl,
          remoteWriteUsername,
          ingestenabled: isIngestUrlChecked,
          vmagenturl: VMAgentUrl,
          metricinstanceid: OCPInstanceId,
        }
      : { enablemetric: false };

    // ✅ include only if checked
    if (enableMetrics && isIngestUrlChecked) {
      metrics.ingestUrl = ingestUrl;
    }

    onChange({
      applicationId: selectedApp?.applicationId,
      applicationName: selectedApp?.name,
      appslug: selectedApp?.appslug,
      metrics,
    });
  }, [
    selectedApp,
    enableMetrics,
    metricsTenancy,
    metricsFile,
    ingestUrl,
    isIngestUrlChecked,
    remoteWriteUrl,
    remoteWriteUsername,
    onChange,
    VMAgentUrl,
    OCPInstanceId,
  ]);

  const handleTenancyChange = async (value: Tenancy | null) => {
    setOcpInstanceOptions([]);
    setMetricsTenancy(value);
    setMetricsFile(null);
    setSelectedInstance(null);
    setRemoteWriteUrl('');
    setOCPInstanceId('');
    setRemoteWriteUsername('');

    if (!value) return;

    const matched = tenancyFileMap.find(t => t.tenancyname === value.name);
    const ocptenantid = matched?.tenancyId;
    const gitrepo = matched?.gitRepo;

    try {
      setLoadingInstances(true);
      const res = await fetch(
        `/api/proxy/cnap/tenancy/getOCPInstanceDetails?ocptenantid=${ocptenantid}&&gitrepo=${gitrepo}`,
      );
      const json = await res.json();
      setOcpInstanceOptions(json || []);
      setLoadingInstances(false);
    } catch (e) {
      setOcpInstanceOptions([]);
      setLoadingInstances(false);
    }
  };

  const handleInstanceChange = async (instance: any | null) => {
    setSelectedInstance(instance);
    setMetricsFile(null);
    setRemoteWriteUrl('');
    setOCPInstanceId('');
    setRemoteWriteUsername('');

    if (!instance || !metricsTenancy) return;

    const matched = tenancyFileMap.find(
      t => t.tenancyname === metricsTenancy.name,
    );
    const gitrepo = matched?.gitRepo;

    try {
      setRemoteWriteUrlLoader(true);
      const res = await fetch(
        `/api/proxy/cnap/tenancy/getOCPInstanceremotewrite?ocpinstanceid=${instance.OCPInstanceId}&&ocpinstancename=${instance.OCPInstanceName}&&gitrepo=${gitrepo}`,
      );
      const json = await res.json();
      if (json.MetricMessage === 'Success') {
        setRemoteWriteUrl(json.RemotewriteURL || '');
        setVMAgentUrl(json.VMAgentURL || '');
        setIngestfieldEnabled(json.IngestfieldEnabled || false);
        setOCPInstanceId(instance.OCPInstanceId);
        setRemoteWriteUsername(json.RemotewriteUsername || '');
        setRemoteWriteUrlLoader(false);
      } else {
        setRemoteWriteUrlLoader(false);
      }
    } catch (e) {
      setRemoteWriteUrlLoader(false);
    }
  };

  return (
    <>
      <DevHubFormControl margin="normal" required={required}>
        <DevHubAutocomplete
          id={`${idSchema.$id}-app`}
          value={selectedApp}
          options={apps}
          loading={loading}
          getOptionLabel={option => option.name}
          onChange={(_, value) => {
            setSelectedApp(value);
            setOcpInstanceOptions([]);
            setEnableMetrics(false);
            setMetricsTenancy(null);
            setMetricsFile(null);
            setSelectedInstance(null);
            setRemoteWriteUrl('');
            setOCPInstanceId('');
            setRemoteWriteUsername('');
          }}
          renderInput={params => (
            <DevHubTextField
              {...params}
              label="Select Application"
              helperText="Choose an application. Please check with the app owner if you are unable to see your app listed. You have to be in any one of these roles:iTRC App Owner, Owner Assistant, iTRC Approver, or DevOps Remediation"
              required
              variant="outlined"
            />
          )}
        />
      </DevHubFormControl>

      <DevHubFormControl margin="normal">
        <DevHubFormControlLabel
          control={
            <DevHubCheckbox
              checked={enableMetrics}
              disabled={selectedApp === null}
              onChange={e => {
                setEnableMetrics(e.target.checked);
                if (!e.target.checked) {
                  setMetricsTenancy(null);
                  setSelectedInstance(null);
                  setMetricsFile(null);
                }
              }}
            />
          }
          label="Enable Metrics"
        />
      </DevHubFormControl>
      {selectedApp && (
        <>
          {enableMetrics && (
            <>
              <DevHubFormControl margin="normal" required>
                <DevHubAutocomplete
                  id={`${idSchema.$id}-metrics-tenancy`}
                  value={metricsTenancy}
                  options={tenancies}
                  loading={loadingTenancies}
                  getOptionLabel={option => option.name}
                  onChange={(_, value) => handleTenancyChange(value)}
                  renderInput={params => (
                    <DevHubTextField
                      {...params}
                      label="Select tenancy for metrics"
                      variant="outlined"
                      required
                      helperText={
                        tenancies.length === 0 &&
                        !loadingTenancies &&
                        'No OCP tenancies available for the selected application. Please reach out to the observability team.'
                      }
                      error={!loadingTenancies && tenancies.length === 0}
                    />
                  )}
                />
              </DevHubFormControl>

              {metricsTenancy && (
                <DevHubFormControl margin="normal" required>
                  <DevHubAutocomplete
                    id={`${idSchema.$id}-metrics-instance`}
                    value={selectedInstance}
                    options={ocpInstanceOptions}
                    loading={loadingInstances}
                    getOptionLabel={option => option.OCPInstanceName}
                    onChange={(_, value) => handleInstanceChange(value)}
                    renderInput={params => (
                      <DevHubTextField
                        {...params}
                        label="Select OCP Instance"
                        variant="outlined"
                        required
                        helperText={
                          ocpInstanceOptions.length === 0 &&
                          !loadingTenancies &&
                          'No OCP Instance available for the selected OCP Tenant. Please reach out to the observability team.'
                        }
                        error={
                          !loadingInstances && ocpInstanceOptions.length === 0
                        }
                      />
                    )}
                  />
                </DevHubFormControl>
              )}

              {selectedInstance && remoteWriteUsername && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginTop: 8,
                  }}
                >
                  <DevHubTypography
                    variant="body2"
                    style={{
                      color: 'black',
                      fontWeight: 'bold',
                      marginRight: 4,
                    }}
                  >
                    Remote Write Username:
                  </DevHubTypography>
                  <DevHubTypography variant="body2" color="textPrimary">
                    {remoteWriteUsername}
                  </DevHubTypography>
                </div>
              )}

              {remoteWriteUrlLoader ? (
                <>Loading..</>
              ) : (
                <>
                  {remoteWriteUrl && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: 8,
                      }}
                    >
                      <DevHubTypography
                        variant="body2"
                        style={{
                          color: 'black',
                          fontWeight: 'bold',
                          marginRight: 4,
                        }}
                      >
                        Remote Write Endpoint:
                      </DevHubTypography>
                      <DevHubTypography variant="body2" color="textPrimary">
                        {remoteWriteUrl}
                      </DevHubTypography>
                    </div>
                  )}

                  {VMAgentUrl && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginTop: 8,
                        }}
                      >
                        <DevHubTypography
                          variant="body2"
                          style={{
                            color: 'black',
                            fontWeight: 'bold',
                            marginRight: 4,
                          }}
                        >
                          VM Agent URL:
                        </DevHubTypography>
                        <DevHubTypography variant="body2" color="textPrimary">
                          {VMAgentUrl}
                        </DevHubTypography>
                      </div>
                    </>
                  )}

                  {IngestfieldEnabled && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          marginTop: 8,
                        }}
                      >
                        <DevHubCheckbox
                          checked={isIngestUrlChecked}
                          onChange={e =>
                            setIsIngestUrlChecked(e.target.checked)
                          }
                          color="primary"
                          id="enable-ingest-url"
                        />
                        <DevHubTypography
                          variant="body2"
                          style={{ marginLeft: 8 }}
                        >
                          Enable Ingest URL
                        </DevHubTypography>
                      </div>
                      {isIngestUrlChecked && (
                        <DevHubTextField
                          label="Ingest URL"
                          variant="outlined"
                          fullWidth
                          value={ingestUrl}
                          onChange={e => {
                            const val = e.target.value;
                            setIngestUrl(val); // always update the state so user sees input
                            validateIngestUrl(val); // this sets errors.ingestUrl
                          }}
                          required={isIngestUrlChecked && ingestUrl === ''}
                          helperText={
                            errors.ingestUrl
                              ? errors.ingestUrl
                              : 'Enter the Ingest URL'
                          }
                          error={!!errors.ingestUrl}
                          style={{ marginTop: 8 }}
                        />
                      )}
                    </>
                  )}

                  {selectedInstance && (
                    <DevHubFormControl margin="normal" required>
                      <DevHubTextField
                        label="Enter Metrics Config File"
                        variant="outlined"
                        required={metricsFile === null}
                        fullWidth
                        value={metricsFile}
                        onChange={e => {
                          const val = e.target.value;
                          const isValid = validateMetricsFile(val); // update errors
                          if (isValid) {
                            setMetricsFile(val); // only set metricsFile if valid
                          } else {
                            setMetricsFile(null); // clear metricsFile if invalid
                          }
                        }}
                        error={!!errors.metricsFile}
                        helperText={errors.metricsFile}
                      />
                    </DevHubFormControl>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};
